import { PaletteMode } from "@mui/material";
import { cloudStorage } from "@tma.js/sdk-react";
import React, { createContext, useContext, useEffect, useState } from "react";

const THEME_MODE_KEY = "app_theme_mode";

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
  setThemeMode: (mode: PaletteMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getStoredThemeMode = async (): Promise<PaletteMode> => {
  try {
    if (cloudStorage.isSupported()) {
      const storedMode = await cloudStorage.getItem(THEME_MODE_KEY);
      if (storedMode === "light" || storedMode === "dark") {
        return storedMode;
      }
    }
  } catch (error) {
    console.warn("Failed to load theme mode from CloudStorage:", error);
  }

  const localMode = localStorage.getItem(THEME_MODE_KEY);
  if (localMode === "light" || localMode === "dark") {
    return localMode;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const saveThemeMode = async (mode: PaletteMode): Promise<void> => {
  try {
    if (cloudStorage.isSupported()) {
      await cloudStorage.setItem(THEME_MODE_KEY, mode);
    }
  } catch (error) {
    console.warn("Failed to save theme mode to CloudStorage:", error);
  }

  localStorage.setItem(THEME_MODE_KEY, mode);
};

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const localMode = localStorage.getItem(THEME_MODE_KEY);
    if (localMode === "light" || localMode === "dark") {
      return localMode;
    }
    return "dark";
  });

  useEffect(() => {
    getStoredThemeMode().then((stored) => {
      if (stored) {
        setMode(stored);
      }
    });
  }, []);

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    saveThemeMode(newMode);
  };

  const setThemeMode = (newMode: PaletteMode) => {
    setMode(newMode);
    saveThemeMode(newMode);
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }
  return context;
};
